/**
 * useShotFullImage — subscribe the big-pixel viewers to a shot's full-res static image.
 *
 * Returns null until (and unless) Unity answers, so the caller's expression stays a fallback chain:
 * the cache thumbnail paints immediately and the full-res frame swaps in when it lands — no blank
 * frame, no spinner, nothing to unwind if the answer never comes (bridge/features/fullImage.ts
 * documents why a missing reply is a legitimate resting state).
 *
 * Eligibility is decided HERE rather than in the module, because it is a question about what the
 * viewer is doing: only an embedded session has a Unity to ask, only a static shot has a blob worth
 * asking for, a video shot renders through the <canvas> path where the still is never shown, and a
 * moving playhead is about to replace the image anyway (see the comment on the gate below). Unity
 * does not check isstaticimageenabled, so this gate is the only thing keeping a request from
 * fetching pixels the viewer would not display (fullImage contract note 2).
 *
 * The request itself is debounced (see FULL_IMAGE_REQUEST_DEBOUNCE_MS below): the playhead gate only
 * covers actual playback, but scrubbing and rapid shot-stepping also land on many shots without
 * pausing, and each landing would otherwise cost a Unity file write plus a multi-megapixel decode for
 * a frame that is about to be replaced. A cache hit is exempt — it is already decoded and costs
 * nothing to serve, so it renders immediately with no timer in the way.
 */

import { useEffect, useState } from 'react';
import { capabilities } from '@/capabilities.ts';
import { requestFullImage, peekShotFullImageCache } from '@/bridge/features/fullImage';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import type { TimelineShot } from '@/types/timeline';

/** How long the selected shot must rest before its full-res image is requested. One number, named,
 *  because both the value and the reasoning (scrub cost, not correctness) belong at the call site
 *  that reads it, not buried in the effect body. */
export const FULL_IMAGE_REQUEST_DEBOUNCE_MS = 250;

export function useShotFullImage(shot: TimelineShot | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const isPlaying = usePlaybackStore(s => s.isPlaying);

  const shotId = shot?.id ?? null;
  // Not while the playhead is moving. Viewer's shot comes from getShotAtTime, so it changes at every
  // shot boundary — and each change would cost a Unity round trip, a File.WriteAllBytes of a
  // distinct {shotId}_full extraction (swept only at webview teardown, so they accumulate for the
  // session), and a multi-megapixel decode every couple of seconds. Playing a static-heavy project
  // would write hundreds of full-res files and hitch on decodes, to add sharpness nobody can
  // resolve while the image is already being replaced. isPlaying is in the effect deps, so pausing
  // runs the normal request path for whatever shot is then selected (served straight from cache if
  // it was fetched before playback started).
  const eligible = capabilities.bridge
    && !isPlaying
    && shotId !== null
    && shot?.hasStaticImage === true
    && !shot?.videoObjectUrl;

  useEffect(() => {
    // Clear first: a shot switch must drop the previous shot's frame in the same commit that
    // subscribes to the new one, or the viewer briefly paints the wrong image at full size.
    setUrl(null);
    if (!eligible || shotId === null) return;

    // A cache hit costs nothing to serve — already decoded, no Unity round trip — so it bypasses the
    // debounce and subscribes immediately (requestFullImage fires the callback synchronously on a hit).
    if (peekShotFullImageCache(shotId) !== undefined) return requestFullImage(shotId, setUrl);

    // Otherwise wait for the shot to rest. A shot change (or eligibility loss — deselect, video,
    // isPlaying, unmount) before the timer fires cancels it via this cleanup, so a shot only scrubbed
    // past never triggers a request at all.
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      unsubscribe = requestFullImage(shotId, setUrl);
    }, FULL_IMAGE_REQUEST_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      unsubscribe?.();
    };
  }, [shotId, eligible]);

  return url;
}
